import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';

/**
 * 司机策略。
 *
 * ★ 这张表管着「司机能不能接单、一次最多几单、要不要先签到」,
 *   而在这之前它**只读不写** —— 调度员撞到「司机已达并发上限」时,
 *   唯一的办法是改数据库。
 *
 * ★ 每一项都写清「改了会怎样」,和功能开关一个道理。
 *   一屏数字输入框而不说明后果的话,人只会照着默认值放着不动;
 *   真需要改的那一天,他也不敢改。
 */

interface Policy {
  driverType: 'EMPLOYEE' | 'CONTRACTOR' | 'CROWD';
  canBeAssigned: boolean;
  canGrab: boolean;
  canReject: boolean;
  acceptTimeoutMinutes: number;
  maxConcurrentOrders: number;
  maxDailyOrders: number | null;
  requireShift: boolean;
  requireDocsVerified: boolean;
  settlementCycle: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'PER_ORDER';
  poolMaskLevel: 'FULL' | 'STREET' | 'AREA';
  poolMaxVisible: number;
  driverCount: number;
}

/**
 * 司机类型的中文名。
 *
 * ★ 导出给排班那一屏用(它要说出「哪几类不看排班」)。
 *   抄一份过去的话,以后加一种司机类型只会有一处被改到。
 */
export const TYPE_LABEL: Record<Policy['driverType'], string> = {
  EMPLOYEE: '自有司机',
  CONTRACTOR: '承包司机',
  CROWD: '众包司机',
};

/**
 * 订单池里给司机看到多细的地址。
 *
 * ★ schema 里管它叫「最大的数据泄露面」。众包司机如果落到「完整」,
 *   等于任何一个注册司机都能把当天所有未接单的门牌地址导出去。
 *   坐标也跟着这个级别降精度。
 */
const MASK_LABEL: Record<Policy['poolMaskLevel'], string> = {
  FULL: '完整地址',
  STREET: '只到路名',
  AREA: '只到区域',
};

const CYCLE_LABEL: Record<Policy['settlementCycle'], string> = {
  DAILY: '每天',
  WEEKLY: '每周',
  MONTHLY: '每月',
  PER_ORDER: '一单一结',
};

/** 每个开关关掉之后会发生什么 —— 照着功能开关那套写法 */
const SWITCHES: Array<{
  key: 'canBeAssigned' | 'canGrab' | 'canReject' | 'requireShift' | 'requireDocsVerified';
  label: string;
  whenOn: string;
  whenOff: string;
}> = [
  {
    key: 'canBeAssigned',
    label: '可以被派单',
    whenOn: '调度可以把单指派给他',
    whenOff: '调度派不了单给他 —— 只能靠他自己抢',
  },
  {
    key: 'canGrab',
    label: '可以抢单',
    whenOn: '他能在订单池里自己领单',
    whenOff: '订单池对他不开放,只能等调度指派',
  },
  {
    key: 'canReject',
    label: '可以拒单',
    whenOn: '接到派单后可以拒掉,单子回到待派',
    whenOff: '派了就是他的,拒不掉 —— 有异议要联系调度',
  },
  {
    key: 'requireShift',
    label: '必须排班',
    // ★ 写清楚**不影响**什么。只写「看不到」的话,运营会以为
    //   打开它等于把人停掉 —— 而派给他的单是照常能接的
    whenOn: '没排班的那天「可领取」里没有单,也上不了线;派给他的单照常接',
    whenOff: '随时能领单,不看排班',
  },
  {
    key: 'requireDocsVerified',
    label: '证件必须已审核',
    whenOn: '驾照等证件没审核通过的接不了单',
    whenOff: '不检查证件',
  },
];

export function DriverPolicies({ canManage }: { canManage: boolean }) {
  const [rows, setRows] = useState<Policy[]>([]);
  const [editing, setEditing] = useState<Policy | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await api<Policy[]>('/driver-policies'));
    } catch {
      /* 没权限时静默 —— 页面按权限渲染 */
    }
  }, []);
  useEffect(() => void load(), [load]);

  if (rows.length === 0) return null;

  return (
    <section>
      <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-1">司机策略</h2>
      <p className="text-sm text-slate-500 mb-4">改这里**立刻生效**,影响的是所有这一类司机</p>

      {err && <div className="mb-3 rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm">{err}</div>}

      <div className="grid gap-3 md:grid-cols-3">
        {rows.map((p) => (
          <div
            key={p.driverType}
            className="rounded-xl border border-slate-200 dark:border-slate-700
                       bg-white dark:bg-slate-800 p-4"
          >
            <div className="flex items-baseline justify-between">
              <span className="font-medium text-slate-900 dark:text-slate-100">
                {TYPE_LABEL[p.driverType]}
              </span>
              {/*
                ★ 说明这一类现在有几个司机。
                  不说的话,人很容易去改一张根本没人用的策略,
                  然后奇怪「怎么没变化」。
              */}
              <span className="text-xs text-slate-400">
                {p.driverCount > 0 ? `${p.driverCount} 人在用` : '暂无司机'}
              </span>
            </div>

            <dl className="mt-2 space-y-1 text-sm">
              <Row label="同时最多接单" value={`${p.maxConcurrentOrders} 单`} />
              <Row
                label="每天最多接单"
                value={p.maxDailyOrders ? `${p.maxDailyOrders} 单` : '不限'}
              />
              <Row label="接单超时" value={`${p.acceptTimeoutMinutes} 分钟`} />
              <Row label="结算周期" value={CYCLE_LABEL[p.settlementCycle]} />
              <Row label="订单池里的地址" value={MASK_LABEL[p.poolMaskLevel]} />
              <Row label="订单池一次最多看到" value={`${p.poolMaxVisible} 单`} />
              {SWITCHES.map((s) => (
                <Row key={s.key} label={s.label} value={p[s.key] ? '是' : '否'} />
              ))}
            </dl>

            {canManage && (
              <button
                onClick={() => setEditing(p)}
                className="mt-3 text-sm text-brand-600 hover:underline"
              >
                修改
              </button>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <PolicyEditor
          policy={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            setErr(null);
            await load();
          }}
          onError={setErr}
        />
      )}
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-800 dark:text-slate-200">{value}</dd>
    </div>
  );
}

function PolicyEditor({
  policy,
  onClose,
  onSaved,
  onError,
}: {
  policy: Policy;
  onClose: () => void;
  onSaved: () => Promise<void>;
  onError: (m: string) => void;
}) {
  const [f, setF] = useState(policy);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api(`/driver-policies/${f.driverType}`, {
        method: 'PUT',
        body: {
          canBeAssigned: f.canBeAssigned,
          canGrab: f.canGrab,
          canReject: f.canReject,
          acceptTimeoutMinutes: f.acceptTimeoutMinutes,
          maxConcurrentOrders: f.maxConcurrentOrders,
          maxDailyOrders: f.maxDailyOrders,
          poolMaskLevel: f.poolMaskLevel,
          poolMaxVisible: f.poolMaxVisible,
          requireShift: f.requireShift,
          requireDocsVerified: f.requireDocsVerified,
          settlementCycle: f.settlementCycle,
        },
      });
      await onSaved();
    } catch (e) {
      const m = e instanceof Error ? e.message : '保存失败';
      setErr(m);
      onError(m);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-md max-h-[90dvh] overflow-auto p-5 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-semibold text-slate-900 dark:text-slate-100">
          {TYPE_LABEL[f.driverType]} · 策略
        </h2>

        <Num
          label="同时最多接单"
          hint="司机手上未送达的单达到这个数就派不进去了"
          value={f.maxConcurrentOrders}
          onChange={(v) => setF({ ...f, maxConcurrentOrders: v })}
        />
        <Num
          label="接单超时(分钟)"
          hint="派单后超过这个时间没接,单子回到待派"
          value={f.acceptTimeoutMinutes}
          onChange={(v) => setF({ ...f, acceptTimeoutMinutes: v })}
        />
        <label className="block text-sm">
          <span className="text-slate-500">每天最多接单</span>
          <div className="text-xs text-slate-400 mb-1">留空表示不限</div>
          <input
            type="number"
            aria-label="每天最多接单"
            value={f.maxDailyOrders ?? ''}
            placeholder="不限"
            onChange={(e) =>
              setF({ ...f, maxDailyOrders: e.target.value === '' ? null : Number(e.target.value) })
            }
            className="w-full rounded-lg border border-slate-300 dark:border-slate-600
                       bg-white dark:bg-slate-800 px-2 py-1.5"
          />
        </label>

        <label className="block text-sm">
          <span className="text-slate-500">订单池里的地址</span>
          {/* 这一栏的后果最重,必须写出来 */}
          <div className="mb-1 text-xs text-slate-400">
            还没人接的单,给这一类司机看到多细的地址。选「完整」等于任何一个
            这类司机都能拿到当天所有未接单的门牌位置
          </div>
          <select
            value={f.poolMaskLevel}
            onChange={(e) =>
              setF({ ...f, poolMaskLevel: e.target.value as Policy['poolMaskLevel'] })
            }
            className="w-full rounded-lg border border-slate-300 dark:border-slate-600
                       bg-white dark:bg-slate-800 px-2 py-1.5"
          >
            {(Object.keys(MASK_LABEL) as Array<Policy['poolMaskLevel']>).map((k) => (
              <option key={k} value={k}>
                {MASK_LABEL[k]}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          <span className="text-slate-500">订单池一次最多看到</span>
          {/*
            ★ 这个字段建表时就有、查询里一直在用,而**界面上一直改不了**。
              后果很具体:司机在地图上把视野挪到自己片区,仍然只看得到
              这么多个图钉 —— 而他打开地图正是为了「看看我这片还有什么能接」。
              数字太小的表现不是报错,是**有些单他永远看不到**。
          */}
          <span className="block text-xs text-slate-400">
            地图上也用这个数。调小了他就看不全自己片区的单
          </span>
          <input
            type="number"
            min={1}
            max={500}
            value={f.poolMaxVisible}
            onChange={(e) => setF({ ...f, poolMaxVisible: Number(e.target.value) })}
            className="w-full rounded-lg border border-slate-300 dark:border-slate-600
                       bg-white dark:bg-slate-800 px-2 py-1.5"
          />
        </label>

        <label className="block text-sm">
          <span className="text-slate-500">结算周期</span>
          <select
            value={f.settlementCycle}
            onChange={(e) =>
              setF({ ...f, settlementCycle: e.target.value as Policy['settlementCycle'] })
            }
            className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-600
                       bg-white dark:bg-slate-800 px-2 py-1.5"
          >
            {(Object.keys(CYCLE_LABEL) as Array<Policy['settlementCycle']>).map((c) => (
              <option key={c} value={c}>
                {CYCLE_LABEL[c]}
              </option>
            ))}
          </select>
        </label>

        <div className="pt-1 space-y-2">
          {SWITCHES.map((s) => (
            <label key={s.key} className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                aria-label={s.label}
                checked={f[s.key]}
                onChange={(e) => setF({ ...f, [s.key]: e.target.checked })}
                className="mt-1"
              />
              <span>
                <span className="text-slate-800 dark:text-slate-200">{s.label}</span>
                {/* 关掉会怎样必须写出来 —— 光一个开关名字,没人敢动它 */}
                <span className="block text-xs text-slate-400">
                  {f[s.key] ? s.whenOn : s.whenOff}
                </span>
              </span>
            </label>
          ))}
        </div>

        {err && <div className="rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm">{err}</div>}

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-3 py-2 rounded-lg text-slate-500 text-sm">
            取消
          </button>
          <button
            onClick={() => void save()}
            disabled={busy}
            className="px-3 py-2 rounded-lg bg-brand-600 text-white text-sm disabled:opacity-50"
          >
            {busy ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Num({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="block text-sm">
      <span className="text-slate-500">{label}</span>
      <div className="text-xs text-slate-400 mb-1">{hint}</div>
      <input
        type="number"
        aria-label={label}
        value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="w-full rounded-lg border border-slate-300 dark:border-slate-600
                   bg-white dark:bg-slate-800 px-2 py-1.5"
      />
    </label>
  );
}
